import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, X, Minimize2, Maximize2, ThumbsUp, ThumbsDown, Star } from 'lucide-react';
import { chatAPI } from '../services/api';

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [config, setConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [lastBotMessageId, setLastBotMessageId] = useState(null);
  const messagesEndRef = useRef(null);

  // Load chatbot config and generate session ID on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || '/api';
        const response = await fetch(`${API_URL}/chat/config`);
        const data = await response.json();
        
        if (data.success && data.data) {
          setConfig(data.data);
          
          // Set welcome message from config
          setMessages([{
            id: 1,
            text: data.data.welcomeMessage || "Hi! I'm LionBot, your virtual assistant. How can I help you today?",
            isBot: true,
            timestamp: new Date()
          }]);
        }
      } catch (error) {
        console.error('Error loading chatbot config:', error);
        // Fallback to default welcome message
        setMessages([{
          id: 1,
          text: "Hi! I'm LionBot, your virtual assistant. How can I help you today?",
          isBot: true,
          timestamp: new Date()
        }]);
      }
    };
    
    loadConfig();
    
    const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    setSessionId(newSessionId);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || isLoading) return;

    const userMessage = {
      id: messages.length + 1,
      text: text,
      isBot: false,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await chatAPI.sendMessage({
        message: text,
        sessionId: sessionId
      });

      if (response.data.success) {
        const botMessage = {
          id: messages.length + 2,
          text: response.data.data.response,
          isBot: true,
          timestamp: new Date(),
          intent: response.data.data.intent,
          confidence: response.data.data.confidence
        };
        
        setMessages(prev => [...prev, botMessage]);
        setLastBotMessageId(botMessage.id);
        
        // Show rating after a few messages
        if (messages.length > 4 && Math.random() > 0.7) {
          setShowRating(true);
        }
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: messages.length + 2,
        text: "I'm sorry, I'm having trouble right now. Please try again or contact our support team at hello@liontekglobal.com",
        isBot: true,
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = () => sendMessage(inputValue);

  const handleRating = async (rating) => {
    try {
      await chatAPI.rateConversation({
        conversationId: lastBotMessageId,
        rating: rating
      });
      setShowRating(false);
      
      // Add a thank you message
      const thankYouMessage = {
        id: messages.length + 1,
        text: "Thank you for your feedback! Is there anything else I can help you with?",
        isBot: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, thankYouMessage]);
    } catch (error) {
      console.error('Error rating conversation:', error);
      setShowRating(false);
    }
  };

  // Use quick replies from config or fallback to defaults
  const quickActions = config?.quickReplies || [
    "What services do you offer?",
    "Show me your portfolio",
    "How can I contact you?",
    "Tell me about your team",
    "What are your products?",
    "What technologies do you use?"
  ];

  const handleQuickAction = (action) => sendMessage(action);

  return (
    <>
      {/* Chatbot Toggle Button */}
      <motion.button
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300"
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        animate={isOpen ? { scale: 0 } : { scale: 1 }}
      >
        <MessageCircle size={24} />
        {messages.filter(m => m.isBot && !m.read).length > 1 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
            {messages.filter(m => m.isBot && !m.read).length - 1}
          </span>
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={`fixed bottom-6 right-6 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 ${
              isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]'
            }`}
            initial={{ opacity: 0, scale: 0.8, y: 100 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 100 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header */}
            <div 
              className="text-white p-4 rounded-t-2xl flex items-center justify-between"
              style={{
                background: config?.theme?.primaryColor 
                  ? `linear-gradient(to right, ${config.theme.primaryColor}, ${config.theme.primaryColor}dd)` 
                  : 'linear-gradient(to right, #f97316, #dc2626)'
              }}
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  {config?.avatarUrl ? (
                    <img src={config.avatarUrl} alt="Bot" className="w-10 h-10 rounded-full" />
                  ) : (
                    <MessageCircle size={20} />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold">{config?.name || 'LionBot'}</h3>
                  <p className="text-xs opacity-90">
                    {isLoading ? 'Typing...' : 'Online'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                >
                  {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 h-96">
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                          message.isBot
                            ? message.isError
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                            : 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                        }`}
                      >
                        <p className="text-sm">{message.text}</p>
                        <p className="text-xs opacity-70 mt-1">
                          {message.timestamp.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                  
                  {isLoading && (
                    <motion.div
                      className="flex justify-start"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <div className="bg-gray-100 text-gray-800 max-w-xs lg:max-w-md px-4 py-2 rounded-2xl">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Rating System */}
                {showRating && (
                  <motion.div
                    className="px-4 py-2 bg-yellow-50 border-t border-yellow-200"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                  >
                    <p className="text-sm text-gray-600 mb-2">How was my response?</p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleRating(5)}
                        className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs hover:bg-green-200 transition-colors"
                      >
                        <ThumbsUp size={12} />
                        <span>Helpful</span>
                      </button>
                      <button
                        onClick={() => handleRating(2)}
                        className="flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs hover:bg-red-200 transition-colors"
                      >
                        <ThumbsDown size={12} />
                        <span>Not helpful</span>
                      </button>
                      <button
                        onClick={() => setShowRating(false)}
                        className="px-3 py-1 text-gray-500 rounded-full text-xs hover:bg-gray-100 transition-colors"
                      >
                        Skip
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Quick Actions */}
                {messages.length <= 2 && (
                  <div className="px-4 py-2 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">Quick questions:</p>
                    <div className="flex flex-wrap gap-1">
                      {quickActions.map((action, index) => (
                        <button
                          key={index}
                          onClick={() => handleQuickAction(action)}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs hover:bg-gray-200 transition-colors"
                        >
                          {action}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Input */}
                <div className="p-4 border-t border-gray-200">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                      placeholder="Type your message..."
                      disabled={isLoading}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50"
                    />
                    <button
                      onClick={handleSend}
                      disabled={isLoading || !inputValue.trim()}
                      className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-full hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatbotWidget;